'use client';

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref'] as const;
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

/**
 * Save any UTM / ref params found in the current URL to cookies.
 */
function saveUTMsToCookies(): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);

  params.forEach((value, key) => {
    if (key.startsWith('utm_') || key === 'ref') {
      document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; secure; samesite=lax`;
    }
  });
}

/**
 * Read all persisted UTM / ref cookies into a plain object.
 */
export function getUTMsFromCookies(): Record<string, string> {
  if (typeof document === 'undefined') return {};

  const utms: Record<string, string> = {};

  document.cookie.split('; ').forEach((cookie) => {
    const [key, ...rest] = cookie.split('=');
    if (key.startsWith('utm_') || key === 'ref') {
      utms[key] = decodeURIComponent(rest.join('='));
    }
  });

  return utms;
}

/**
 * Append persisted UTM params to a URL string (only if not already present).
 */
export function appendUTMsToURL(url: string): string {
  try {
    const utms = getUTMsFromCookies();
    if (!Object.keys(utms).length) return url;

    const link = new URL(url, window.location.origin);
    Object.entries(utms).forEach(([key, val]) => {
      if (!link.searchParams.has(key)) link.searchParams.set(key, val);
    });

    return link.pathname + '?' + link.searchParams.toString() + link.hash;
  } catch {
    return url;
  }
}

/**
 * Hook that persists UTM parameters across client-side navigations.
 *
 * On every route change it:
 *  1. Saves any UTM / ref query-params from the current URL into cookies (30-day TTL)
 *  2. Re-applies saved UTMs to all internal anchor links on the page
 *
 * It also patches history.pushState / replaceState so that programmatic
 * navigation (router.push, router.replace) automatically includes UTMs.
 */
export function useUTMPersistence(): void {
  const pathname = usePathname();

  // Patch history API once to intercept all programmatic navigations
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);

    const patchUrl = (url: string | URL | null | undefined): string | URL | null | undefined => {
      if (!url) return url;
      const utms = getUTMsFromCookies();
      if (!Object.keys(utms).length) return url;

      try {
        const urlObj = new URL(String(url), window.location.origin);
        if (urlObj.origin !== window.location.origin) return url;

        let changed = false;
        Object.entries(utms).forEach(([key, val]) => {
          if (!urlObj.searchParams.has(key)) {
            urlObj.searchParams.set(key, val);
            changed = true;
          }
        });

        if (!changed) return url;
        return urlObj.pathname + urlObj.search + urlObj.hash;
      } catch {
        return url;
      }
    };

    history.pushState = function (state, title, url) {
      return originalPushState(state, title, patchUrl(url));
    };
    history.replaceState = function (state, title, url) {
      return originalReplaceState(state, title, patchUrl(url));
    };

    return () => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  useEffect(() => {
    // Save UTMs from current URL into cookies
    saveUTMsToCookies();

    // Re-append UTMs to all internal <a> links on the page
    const applyUTMsToLinks = () => {
      const utms = getUTMsFromCookies();
      if (!Object.keys(utms).length) return;

      document.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((anchor) => {
        try {
          const href = anchor.getAttribute('href');
          if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

          // Only modify internal links
          const url = new URL(href, window.location.origin);
          if (url.origin !== window.location.origin) return;

          let changed = false;
          Object.entries(utms).forEach(([key, val]) => {
            if (!url.searchParams.has(key)) {
              url.searchParams.set(key, val);
              changed = true;
            }
          });

          if (changed) {
            anchor.setAttribute('href', url.pathname + '?' + url.searchParams.toString() + url.hash);
          }
        } catch {
          // Skip malformed URLs
        }
      });
    };

    // Delay DOM modifications until well after React hydration completes
    const timer = setTimeout(applyUTMsToLinks, 3000);

    return () => clearTimeout(timer);
  }, [pathname]);
}
